
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { ITMGMTLogo } from '@/components/logo/ITMGMTLogo';
import { supabase } from '@/integrations/supabase/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Loader2 } from 'lucide-react';
import { getSubdomainType } from '@/utils/subdomainUtils';

const loginSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
});

const EmployeePortal: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  // Clear any existing auth session when landing on the employee portal
  // This helps break redirect loops and ensures a clean login state
  useEffect(() => {
    const clearSession = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        
        if (sessionData.session) {
          console.log('Existing session found on portal page, clearing to prevent redirect loops');
          await supabase.auth.signOut();
        }
      } catch (error) {
        console.error('Error checking/clearing session:', error);
      }
    };
    
    // Only clear session if we're on the exact portal path
    // This prevents clearing during normal portal usage
    if (location.pathname === '/portal') {
      clearSession();
    }
  }, [location.pathname]);

  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const handleLogin = async (values: z.infer<typeof loginSchema>) => {
    setIsLoading(true);
    try {
      console.log(`Employee login attempt for email: ${values.email}`);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });

      if (error) {
        throw error;
      }

      const user = data.user;

      // Check user role in client_onboarding table
      const { data: onboardingData, error: onboardingError } = await supabase
        .from('client_onboarding')
        .select('role, status, id')
        .eq('email', values.email)
        .maybeSingle();
      
      if (onboardingError) {
        throw onboardingError;
      }

      // Check if IDs match between auth.users and client_onboarding
      if (onboardingData && user && user.id !== onboardingData.id) {
        console.warn(`ID mismatch detected: auth.users ID (${user.id}) doesn't match client_onboarding ID (${onboardingData.id})`);
      }

      if (!onboardingData) {
        console.log(`No client_onboarding record found for email: ${values.email}`);
      }

      // Only allow employee login
      if (!onboardingData || onboardingData.role !== 'admin') {
        // Sign out immediately if not an employee
        await supabase.auth.signOut();
        throw new Error('Access denied. This portal is for employees only.');
      }

      toast({
        title: 'Login successful',
        description: 'You have been logged in successfully',
      });

      // Immediately redirect to admin dashboard - CRITICAL FIX
      console.log('Admin login successful via employee portal, redirecting to /admin');
      navigate('/admin', { replace: true });
    } catch (error: any) {
      console.error('Login error:', error);
      toast({
        title: 'Login failed',
        description: error.message || 'There was a problem signing in',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Employee Portal - IT Management Solutions"
        description="Employee login portal for IT Management Solutions staff."
        canonicalUrl="https://itmgmtsol.com/portal"
      />
      <Navbar />
      
      <div className="flex-grow flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-md p-8 border border-gray-100 max-w-md w-full">
          <div className="flex justify-center mb-6">
            <ITMGMTLogo className="h-16" />
          </div>
          
          <h1 className="text-2xl font-bold text-itmgmt-dark-blue mb-2 text-center">Employee Portal</h1>
          <p className="text-gray-500 text-center mb-6">Access restricted to IT Management Solutions employees only.</p>
          
          <Form {...loginForm}>
            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
              <FormField
                control={loginForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="employee@itmgmtsol.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={loginForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="flex justify-center mt-6">
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full max-w-xs"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Logging in...
                    </span>
                  ) : 'Employee Login'}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default EmployeePortal;
